import React from "react";
import { Box, Container, Typography, Button, Stack } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import heroBg from "../../Assets/Hero.svg";
import useResponsive from "../../Hooks/useResponsive";

const HeroSection = () => {
  const { isExtraLarge } = useResponsive();
  const navigate = useNavigate();
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const slideUpVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const wordVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: { xs: "480px", md: "560px" },
        backgroundImage: `url(${heroBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        alignItems: "flex-start",
        pt: { xs: "64px", sm: "72px", md: "80px", lg: "100px" },
        pb: { xs: "64px", sm: "72px", md: "80px" },
        boxSizing: "border-box",
        position: "relative",
        // Optional: darken overlay to ensure text readability if needed
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.2)", // Subtle overlay
          zIndex: 1,
        },
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: { xs: "100%", lg: "1280px", xl: "1400px" },
          px: "32px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{
            maxWidth: "768px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            alignItems: "flex-start",
          }}
        >
          <motion.div variants={wordVariants}>
            <Typography
              variant="h1"
              sx={{
                fontFamily: "Manrope",
                fontWeight: 800,
                fontSize: { xs: "32px", sm: "42px", md: "72px" },
                lineHeight: { xs: "40px", sm: "50px", md: "84px" },
                color: "#FFFFFF",
                letterSpacing: { xs: "-1.6px", md: "-4px" },
                textTransform: "none",
                verticalAlign: "middle",
                width: { md: "768px" },
                height: "auto",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                textAlign: "left",
              }}
            >
              <Box component="span">Crafting Your</Box>

              <Box component="span">
                <Box
                  component="span"
                  sx={{
                    color: "#FF5722",
                    letterSpacing: "0px",
                    lineHeight: "100%",
                  }}
                >
                  Vision
                </Box>{" "}
                into
              </Box>
              <Box component="span">Reality</Box>
            </Typography>
          </motion.div>

          {/* Subtitle / Paragraph */}
          <motion.div variants={slideUpVariants} style={{ width: "100%" }}>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 300,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: { xs: "24px", sm: "26px", md: "28px" },
                color: "rgba(255, 255, 255, 0.9)",
                maxWidth: "672px",
                width: { md: "672px" },
                height: "auto",
                letterSpacing: "0px",
                verticalAlign: "middle",
                textAlign: "left",
              }}
            >
              From bespoke interior designs for residential and commercial
              spaces to large-scale infrastructure construction, we build the
              future you envision.
            </Typography>
          </motion.div>

          {/* Action Buttons */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: "16px", sm: "20px" }}
            sx={{ mt: { xs: "12px", md: "14px" } }}
          >
            <motion.div variants={slideUpVariants} style={{ width: "100%" }}>
              <Button
                variant="contained"
                onClick={() => navigate("/interior-design")}
                sx={{
                  width: { xs: "100%", sm: "280px" },
                  height: { xs: "50px", sm: "54px" },
                  background:
                    "linear-gradient(135deg, #B02F00 0%, #FF5722 100%)",
                  borderRadius: "6px",
                  padding: "12px 24px",
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "14px",
                  textTransform: "none",
                  boxShadow: "0px 6px 20px rgba(176, 47, 0, 0.28)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #FF5722 0%, #B02F00 100%)",
                    transform: "translateY(-2px)",
                    boxShadow: "0px 8px 24px rgba(176, 47, 0, 0.4)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Explore Our Interior Design
              </Button>
            </motion.div>

            <motion.div variants={slideUpVariants} style={{ width: "100%" }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/projects")}
                sx={{
                  width: { xs: "100%", sm: "156px" },
                  height: { xs: "50px", sm: "54px" },
                  borderRadius: "6px",
                  border: "1px solid rgba(255, 255, 255, 0.4)",
                  backdropFilter: "blur(12px)",
                  padding: "12px 24px",
                  color: "#FFFFFF",
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "14px",
                  textTransform: "none",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    border: "1px solid #FFFFFF",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Our Projects
              </Button>
            </motion.div>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
};

export default HeroSection;
